import React, { useMemo, useEffect, useState } from 'react';
import { ComparisonListStatus } from '@navi-toolbox/ui-components';
import {
  getDisplayedName,
  getPrimaryPrice,
  getProductBelong,
  getProductId,
  getProductPriceCategory,
} from 'features/product-info/ducks/selectors';
import { useTranslation } from 'react-i18next';
import { useBreakpoints } from 'common/hooks';
import { analyticsUtils } from 'utils';
import { useDispatch, useSelector } from 'react-redux';
import { getCompareCategory } from 'features/product-info';
import {
  getCompareListsError,
  getIsInComparison,
  getProductsCompareLists,
  productsCompareListsActions,
} from 'features/products-compare';
import { getLinkedPagesBrand } from 'features/linked-pages';
import { getProductsCompareListLimit, getProductsCompareListsLimit } from 'common/selectors';
import { GA_ADD_TO_COMPARISON_LIST, GA_REMOVE_FROM_COMPARISON_LIST } from './constants';
import { CompareButtonWithTooltip } from './compare-button-with-tooltip';
import { useComparisonListStatus } from './hooks';

export const PdpComparison: React.FC = () => {
  const dispatch = useDispatch();
  const breakpoints = useBreakpoints();
  const isDesktop = useMemo(() => breakpoints?.largeDesktop || breakpoints?.desktop, [breakpoints]);
  const { t } = useTranslation();
  const compareCategory = useSelector(getCompareCategory);
  const compareLists = useSelector(getProductsCompareLists) || [];
  const compareListLimit = useSelector(getProductsCompareListLimit);
  const compareListsLimit = useSelector(getProductsCompareListsLimit);
  const groupId = compareCategory?.familyId || null;
  const productId = useSelector(getProductId);
  const productName = useSelector(getDisplayedName);
  const { price, prevPrice, discountPercent } = useSelector(getPrimaryPrice);
  const isActiveFromStore = useSelector(getIsInComparison(groupId, productId || ''));
  const error = useSelector(getCompareListsError);
  const { name: brandName } = useSelector(getLinkedPagesBrand) || {};
  const productBelong = useSelector(getProductBelong);
  const productTag = useSelector(getProductPriceCategory);

  const [optimisticIsActive, setOptimisticIsActive] = useState(isActiveFromStore);
  const { items, showStatus, hideStatus, setClickHandler } = useComparisonListStatus();

  useEffect(() => {
    setOptimisticIsActive(isActiveFromStore);
  }, [isActiveFromStore]);

  const handleAddToCompare = () => {
    const currentGroup = compareLists?.find((list) => list.groupId === groupId);
    const isProductListCountInvalid = currentGroup && currentGroup.products.length >= compareListLimit;
    const isListsCountInvalid = !currentGroup && compareLists.length >= compareListsLimit;
    if (!compareLists.length || !(isProductListCountInvalid || isListsCountInvalid)) {
      setOptimisticIsActive(true);
      dispatch(productsCompareListsActions.SEND_PRODUCTS_COMPARE_LIST_ACTION({ action: 'add' }));
      showStatus(error ? 'error' : 'added');
    } else {
      if (isProductListCountInvalid) {
        showStatus('productListCountAlert');
      }
      if (isListsCountInvalid) {
        showStatus('listCountAlert');
      }
    }
  };
  const handleRemoveFromCompare = () => {
    setOptimisticIsActive(false);
    dispatch(productsCompareListsActions.SEND_PRODUCTS_COMPARE_LIST_ACTION({ action: 'delete' }));
    showStatus(error ? 'error' : 'removed');
  };
  const onClick = () => {
    analyticsUtils.sendEvent(
      optimisticIsActive ? GA_REMOVE_FROM_COMPARISON_LIST : GA_ADD_TO_COMPARISON_LIST,
      {
        elementType: 'button',
        elementBlock: 'productDetail',
        elementName: 'comparisonList',
        category: compareCategory?.name,
        brand: brandName,
        productName,
        productId,
        productDiscount: discountPercent,
        productPrice: price?.toFixed(2),
        productPrevPrice: prevPrice ? prevPrice.toFixed(2) : null,
        productBelong: productBelong,
        productTag: productTag,
      },
    );
    if (optimisticIsActive) {
      handleRemoveFromCompare();
    } else {
      handleAddToCompare();
    }
  };
  const handleMoveToComparePage = () => {
    hideStatus();
    analyticsUtils.sendEvent('GA:clickButton', {
      elementType: 'button',
      elementBlock: 'productDetail / pop-up',
      elementName: 'comparisonList',
      elementText: 'Перейти',
    });
  };

  setClickHandler((statusType) => {
    switch (statusType) {
      case 'added':
      case 'productListCountAlert':
      case 'listCountAlert':
        handleMoveToComparePage();
        break;
      case 'removed':
      case 'error':
        onClick();
        break;
    }
  });

  const buttonText = optimisticIsActive ? t('ui.buttons.compareAdded') : t('ui.buttons.compareAdd');
  const tipText = optimisticIsActive
    ? t('ui.buttons.compare-tooltip-remove')
    : t('ui.buttons.compare-tooltip-add');

  const [hasSentErrorEvent, setHasSentErrorEvent] = useState(false);
  const compareErrorText = t('ui.snackbar.compareErrorText');

  useEffect(() => {
    if (error) {
      if (!hasSentErrorEvent) {
        showStatus('error');
        analyticsUtils.sendEvent('GA:show', {
          elementType: 'block',
          elementBlock: 'productDetail',
          elementName: 'comparisonList',
          elementText: `error / ${compareErrorText}`,
        });
        setHasSentErrorEvent(true);
      }
    } else {
      setHasSentErrorEvent(false);
    }
  }, [error, hasSentErrorEvent, compareErrorText]);

  return (
    <>
      <ComparisonListStatus items={items} />
      <CompareButtonWithTooltip
        active={optimisticIsActive}
        onClick={onClick}
        buttonText={buttonText}
        tipText={tipText}
        isDesktop={!!isDesktop}
      />
    </>
  );
};
